import { memo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';

import { Fields } from 'types/fields';
import { Button } from 'components/common/button';
import Text from 'components/common/text';
import TextField from 'components/common/text-field';
import TextFieldPassword from 'components/common/text-field-password';
import TextFieldPhone from 'components/common/text-field-phone';
import { SignUpWithCredentialsForm } from './styles';
import { signUpSchema } from 'utils/validations';
import { getAddressObj } from 'components/google-places/useGooglePlaces';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import Select from 'components/common/select';
import { changeStep, fillNewUser } from 'store/slices/onboarding';
import { onboardingSelector } from 'store/slices/onboarding/selector';

function SignUpWithCredentials() {
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const { newUser } = useAppSelector(onboardingSelector);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<Fields>({
    mode: 'onChange',
    resolver: yupResolver(signUpSchema),
  });

  const onSubmit: SubmitHandler<Fields> = async (data) => {
    const fullAddress = query.address;
    try {
      if (typeof fullAddress === 'string') {
        const addressObj = await getAddressObj(fullAddress);
        dispatch(
          fillNewUser({
            email: data.signUpEmail,
            fullName: `${data.signUpFirstName} ${data.signUpLastName}`,
            password: data.signUpPassword,
            phoneNumber: data.signUpPhone.split('-').join(''),
            usagePurpose: data.signUpUsagePurpose,
            address: fullAddress,
            addressObj,
          })
        );
        dispatch(changeStep('stepListPrice'));
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <SignUpWithCredentialsForm onSubmit={handleSubmit(onSubmit)}>
      <TextField
        type="text"
        name="signUpEmail"
        label="Email"
        register={register}
        error={errors.signUpEmail}
        placeholder="Enter your email"
        initialValue={newUser?.email}
      />
      <TextField
        type="text"
        name="signUpFirstName"
        label="First name"
        register={register}
        error={errors.signUpFirstName}
        placeholder="Enter your first name"
        initialValue={newUser?.fullName.split(' ')[0]}
      />
      <TextField
        type="text"
        name="signUpLastName"
        label="Last name"
        register={register}
        error={errors.signUpLastName}
        placeholder="Enter your last name"
        initialValue={newUser?.fullName.split(' ')[1]}
      />
      <TextFieldPhone
        control={control}
        name="signUpPhone"
        label="Phone"
        error={errors.signUpPhone}
        initialValue={newUser?.phoneNumber}
      />
      <TextFieldPassword
        type="password"
        name="signUpPassword"
        label="Password"
        register={register}
        error={errors.signUpPassword}
        placeholder="Enter your password"
        initialValue={newUser?.password}
      />

      <Select
        control={control}
        name="signUpUsagePurpose"
        label="How would you like to use Richr"
        error={errors.signUpUsagePurpose}
        initialValue={newUser?.usagePurpose}
      />

      <Button type="submit">
        <Text type="mode3" textTransform="uppercase">
          Sign Up
        </Text>
      </Button>
    </SignUpWithCredentialsForm>
  );
}

export default memo(SignUpWithCredentials);
